import { useCallback, useMemo, type Dispatch, type SetStateAction } from "react";

import { MagnifyingGlassIcon, PlusIcon, XIcon } from "@phosphor-icons/react";
import { useNavigate } from "@tanstack/react-router";

import Beacon from "@gx/dls/beacons/Beacon";
import Button, { ButtonSize, ButtonVariant } from "@gx/dls/buttons/Button";
import FlexItem from "@gx/dls/containers/FlexItem";
import FlexWrapper, { AlignItems, FlexDirection } from "@gx/dls/containers/FlexWrapper";
import HorizontalDivider from "@gx/dls/dividers/HorizontalDivider";
import CheckboxInput from "@gx/dls/inputs/CheckboxInput";
import MultiSelectInput, { PinnedOptions } from "@gx/dls/inputs/MultiSelectInput";
import SelectInput, { SelectInputOption } from "@gx/dls/inputs/SelectInput";
import TextInput from "@gx/dls/inputs/TextInput";
import Text, { TextVariant, TextWeight } from "@gx/dls/text/Text";
import { SourceStatus, SourceType } from "@gx/ts-contracts/services/database/models/public_pb";
import { SortBy, SortOrder } from "@gx/ts-contracts/shared/sorting_pb";

import BaseHeader from "@/layouts/components/BaseHeader";
import BaseToolbar from "@/layouts/components/BaseToolbar";

import { SORT_BY_OPTIONS, SORT_ORDER_OPTIONS } from "@/pages/constants";
import { ALL_SOURCES } from "@/pages/sources/components/create/constants";
import SourceIcon, { SourceIconSize } from "@/pages/sources/components/SourceIcon";
import {
  FILTERABLE_SOURCE_STATUSES,
  SOURCE_STATUS_TO_BEACON_VARIANT_MAP,
  SOURCE_STATUS_TO_TEXT_MAP,
  SOURCE_STATUS_TO_TEXT_VARIANT_MAP,
  SOURCE_TYPE_TO_LABEL_MAP,
  SOURCE_VIEW_TO_ICON_MAP,
  SourcesView,
} from "@/pages/sources/constants";

const FILTERABLE_SOURCE_TYPES = ALL_SOURCES.filter((s) => s !== SourceType.UNSPECIFIED);

interface SourcesPageHeaderProps {
  isLoading: boolean;
  search: string;
  sortBy: SortBy;
  sortOrder: SortOrder;
  onSearchChange: (search: string) => void;
  onSortByChange: (sortBy: SortBy) => void;
  onSortOrderChange: (sortOrder: SortOrder) => void;
  sourceTypes: SourceType[];
  onSourceTypesChange: (sourceTypes: SourceType[]) => void;
  sourceStatuses: SourceStatus[];
  onSourceStatusesChange: (sourceStatuses: SourceStatus[]) => void;
  sourceView: SourcesView;
  onSourceViewChange: Dispatch<SetStateAction<SourcesView>>;
  includeDeleted: boolean;
  onIncludeDeletedChange: (includeDeleted: boolean) => void;
  onClose?: () => void;
}

const SourcesPageHeader = ({
  isLoading,
  search,
  sortBy,
  sortOrder,
  onSearchChange,
  onSortByChange,
  onSortOrderChange,
  sourceTypes,
  onSourceTypesChange,
  sourceStatuses,
  onSourceStatusesChange,
  sourceView,
  onSourceViewChange,
  includeDeleted,
  onIncludeDeletedChange,
  onClose,
}: SourcesPageHeaderProps) => {
  const navigate = useNavigate();

  // Source type options with icons
  const sourceTypeOptions: SelectInputOption[] = useMemo(
    () =>
      FILTERABLE_SOURCE_TYPES.map((sourceType) => ({
        id: sourceType.toString(),
        label: SOURCE_TYPE_TO_LABEL_MAP[sourceType],
        value: sourceType,
        icon: <SourceIcon sourceType={sourceType} size={SourceIconSize.XSMALL} />,
      })),
    [],
  );

  // Source type pinned options
  const sourceTypePinnedOptions: PinnedOptions[] = useMemo(
    () => [
      {
        id: "all-source-types",
        label: "All source types",
        optionIds: FILTERABLE_SOURCE_TYPES.map((sourceType) => sourceType.toString()),
      },
    ],
    [],
  );

  // Selected source type options
  const selectedSourceTypeOptions = useMemo(() => {
    const selectedIds = new Set(sourceTypes.map((sourceType) => sourceType.toString()));
    return sourceTypeOptions.filter((option) => selectedIds.has(option.id));
  }, [sourceTypes, sourceTypeOptions]);

  // Source status options with beacon icons
  const sourceStatusOptions: SelectInputOption[] = useMemo(
    () =>
      FILTERABLE_SOURCE_STATUSES.map((status) => ({
        id: status.toString(),
        label: SOURCE_STATUS_TO_TEXT_MAP[status],
        value: status,
        icon: <Beacon variant={SOURCE_STATUS_TO_BEACON_VARIANT_MAP[status]} />,
        variant: SOURCE_STATUS_TO_TEXT_VARIANT_MAP[status],
      })),
    [],
  );

  // Source status pinned options
  const sourceStatusPinnedOptions: PinnedOptions[] = useMemo(
    () => [
      {
        id: "all-statuses",
        label: "All statuses",
        optionIds: FILTERABLE_SOURCE_STATUSES.map((status) => status.toString()),
      },
    ],
    [],
  );

  // Selected source status options
  const selectedSourceStatusOptions = useMemo(() => {
    const selectedIds = new Set(sourceStatuses.map((status) => status.toString()));
    return sourceStatusOptions.filter((option) => selectedIds.has(option.id));
  }, [sourceStatuses, sourceStatusOptions]);

  const handleViewChange = useCallback(
    () =>
      onSourceViewChange((currentView) =>
        currentView === SourcesView.LIST ? SourcesView.GRID : SourcesView.LIST,
      ),
    [onSourceViewChange],
  );

  const handleNewSourceClick = useCallback(() => {
    void navigate({
      to: ".",
      search: (prev) => ({ ...prev, createSource: true }),
    });
  }, [navigate]);

  const handleSortByChange = useCallback(
    (value: SelectInputOption) => {
      onSortByChange(value.value as SortBy);
    },
    [onSortByChange],
  );

  const handleSortOrderChange = useCallback(
    (value: SelectInputOption) => {
      onSortOrderChange(value.value as SortOrder);
    },
    [onSortOrderChange],
  );

  const handleSearchClear = useCallback(() => {
    onSearchChange("");
  }, [onSearchChange]);

  const handleSourceTypesChange = useCallback(
    (values: SelectInputOption[]) => {
      onSourceTypesChange(values.map((v) => v.value as SourceType));
    },
    [onSourceTypesChange],
  );

  const handleResetSourceTypes = useCallback(() => {
    onSourceTypesChange(FILTERABLE_SOURCE_TYPES);
  }, [onSourceTypesChange]);

  const handleSourceStatusesChange = useCallback(
    (values: SelectInputOption[]) => {
      onSourceStatusesChange(values.map((v) => v.value as SourceStatus));
    },
    [onSourceStatusesChange],
  );

  const handleResetSourceStatuses = useCallback(() => {
    onSourceStatusesChange(FILTERABLE_SOURCE_STATUSES);
  }, [onSourceStatusesChange]);

  const renderSourceTypeSelectedContent = useCallback(
    (selectedOptions: SelectInputOption[], placeholder: string) => {
      if (selectedOptions.length === FILTERABLE_SOURCE_TYPES.length) {
        return (
          <Text variant={TextVariant.PRIMARY} weight={TextWeight.MEDIUM} isEllipsis>
            All source types
          </Text>
        );
      }
      if (selectedOptions.length === 0) {
        return (
          <Text variant={TextVariant.TERTIARY} weight={TextWeight.MEDIUM} isEllipsis>
            {placeholder}
          </Text>
        );
      }
      if (selectedOptions.length === 1) {
        const sourceType = selectedOptions[0].value as SourceType;
        return (
          <FlexWrapper alignItems={AlignItems.CENTER} gap={6}>
            <FlexItem shrink={0}>
              <SourceIcon sourceType={sourceType} size={SourceIconSize.XSMALL} />
            </FlexItem>
            <Text variant={TextVariant.PRIMARY} weight={TextWeight.MEDIUM} isEllipsis>
              {selectedOptions[0].label}
            </Text>
          </FlexWrapper>
        );
      }
      return (
        <Text variant={TextVariant.PRIMARY} weight={TextWeight.MEDIUM} isEllipsis>
          {selectedOptions.length} source types
        </Text>
      );
    },
    [],
  );

  const renderSourceStatusSelectedContent = useCallback(
    (selectedOptions: SelectInputOption[], placeholder: string) => {
      if (selectedOptions.length === FILTERABLE_SOURCE_STATUSES.length) {
        return (
          <Text variant={TextVariant.PRIMARY} weight={TextWeight.MEDIUM} isEllipsis>
            All statuses
          </Text>
        );
      }
      if (selectedOptions.length === 0) {
        return (
          <Text variant={TextVariant.TERTIARY} weight={TextWeight.MEDIUM} isEllipsis>
            {placeholder}
          </Text>
        );
      }
      if (selectedOptions.length === 1) {
        const status = selectedOptions[0].value as SourceStatus;
        return (
          <FlexWrapper alignItems={AlignItems.CENTER} gap={6}>
            <FlexItem shrink={0}>
              <Beacon variant={SOURCE_STATUS_TO_BEACON_VARIANT_MAP[status]} />
            </FlexItem>
            <Text variant={TextVariant.PRIMARY} weight={TextWeight.MEDIUM} isEllipsis>
              {selectedOptions[0].label}
            </Text>
          </FlexWrapper>
        );
      }
      return (
        <Text variant={TextVariant.PRIMARY} weight={TextWeight.MEDIUM} isEllipsis>
          {selectedOptions.length} statuses
        </Text>
      );
    },
    [],
  );

  return (
    <FlexWrapper direction={FlexDirection.COLUMN} fillWidth>
      <FlexWrapper padding="12px 16px" fillWidth>
        <BaseHeader
          title="Sources"
          actions={[
            <Button
              key="new-source"
              label="New source"
              icon={PlusIcon}
              size={ButtonSize.SMALL}
              onClick={handleNewSourceClick}
              isDisabled={isLoading}
            />,
            onClose && (
              <Button
                key="close"
                icon={XIcon}
                variant={ButtonVariant.TERTIARY}
                size={ButtonSize.SMALL}
                onClick={onClose}
              />
            ),
          ].filter(Boolean)}
        />
      </FlexWrapper>
      <HorizontalDivider />
      <FlexWrapper padding="8px 12px" fillWidth>
        <BaseToolbar
          leadingActions={[
            <Button
              key="view"
              variant={ButtonVariant.TERTIARY}
              icon={SOURCE_VIEW_TO_ICON_MAP[sourceView]}
              onClick={handleViewChange}
            />,
            <TextInput
              key="search"
              placeholder="Search"
              leading={{ icon: MagnifyingGlassIcon }}
              trailing={search ? { icon: XIcon, onClick: handleSearchClear } : undefined}
              width={240}
              onChange={onSearchChange}
              value={search}
              isDisabled={isLoading}
            />,
            <MultiSelectInput
              key="type"
              options={sourceTypeOptions}
              placeholder="Source type"
              value={selectedSourceTypeOptions}
              onChange={handleSourceTypesChange}
              onReset={handleResetSourceTypes}
              pinnedOptions={sourceTypePinnedOptions}
              renderSelectedContent={renderSourceTypeSelectedContent}
              width={160}
              dropdownWidth={240}
            />,
            <MultiSelectInput
              key="status"
              options={sourceStatusOptions}
              placeholder="Status"
              value={selectedSourceStatusOptions}
              onChange={handleSourceStatusesChange}
              onReset={handleResetSourceStatuses}
              pinnedOptions={sourceStatusPinnedOptions}
              renderSelectedContent={renderSourceStatusSelectedContent}
              width={140}
              dropdownWidth={200}
            />,
            <CheckboxInput
              key="include-deleted"
              label="Include deleted"
              isChecked={includeDeleted}
              onChange={onIncludeDeletedChange}
            />,
          ]}
          trailingActions={[
            <SelectInput
              key="sort-by"
              placeholder="Sort by"
              options={SORT_BY_OPTIONS}
              value={SORT_BY_OPTIONS.find((option) => option.value === sortBy)}
              onChange={handleSortByChange}
              dropdownWidth={200}
            />,
            <SelectInput
              key="sort-order"
              placeholder="Sort order"
              options={SORT_ORDER_OPTIONS}
              value={SORT_ORDER_OPTIONS.find((option) => option.value === sortOrder)}
              onChange={handleSortOrderChange}
              dropdownWidth={200}
            />,
          ]}
        />
      </FlexWrapper>
    </FlexWrapper>
  );
};

export default SourcesPageHeader;
